import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { Observable, filter, from } from 'rxjs';
import Swal, { SweetAlertCustomClass, SweetAlertOptions, SweetAlertResult } from "sweetalert2";
import { DATE_FORMAT } from './app.constant';
import { ConfirmDeleteAllAlertComponent } from './shared/ui/confirm-delete-all-alert/confirm-delete-all-alert.component';

export const YoungPeopleLiceseId = 3;
// Legacy back-compat shim. The terminology system (TerminologyService +
// TermPipe) is the source of truth — see app.component.ts where it pushes the
// resolved contact_singular / contact_plural values in here on login and again
// whenever the admin saves overrides. Existing {{ contactLabelSingular }}
// consumers continue to work; new templates should prefer the term pipe.
export class App {
  private static $contactLabelSingular = 'Contact';
  private static $contactLabelPlural = 'Contacts';

  static set contactLabelSingular(label: string) {
    this.$contactLabelSingular = label;
  }

  static get contactLabelSingular() {
    return this.$contactLabelSingular;
  }

  static set contactLabelPlural(label: string) {
    this.$contactLabelPlural = label;
  }

  static get contactLabelPlural() {
    return this.$contactLabelPlural;
  }

}

export const floorToNearest = (amount: number, precision: number) => {
  return Math.floor(amount / precision) * precision;
};

export const ceilToNearest = (amount: number, precision: number) => {
  return Math.ceil(amount / precision) * precision;
};

export const unsaveConfirm = (message?: string) => {
  if (confirm(message ?? 'You have unsaved changes! If you leave, your changes will be lost.')) {
    return true;
  } else {
    return false;
  }
};

export const sortByValueExists = (array: any[], key: string, values: string[]): any[] => {
  return array.sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (values.includes(x) && !values.includes(y)) {
      return -1;
    } else if (!values.includes(x) && values.includes(y)) {
      return 1;
    } else {
      return 0;
    }
  });
};

export const isToday = (someDate: Date): boolean => {
  const today = new Date();
  return someDate.getDate() === today.getDate() &&
    someDate.getMonth() === today.getMonth() &&
    someDate.getFullYear() === today.getFullYear();
};

export const isYesterday = (someDate: Date): boolean => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return someDate.getDate() === yesterday.getDate() &&
    someDate.getMonth() === yesterday.getMonth() &&
    someDate.getFullYear() === yesterday.getFullYear();
};

export const isDate = (input: string): boolean => {
  var dateRegex = /^\d{4}\-(0?[1-9]|1[012])\-(0?[1-9]|[12][0-9]|3[01])$/;
  return dateRegex.test(input);
}

export const isDateTime = (input: string): boolean => {
  var dateRegex = /^\d{4}\-(0?[1-9]|1[012])\-(0?[1-9]|[12][0-9]|3[01])T(\d{2}):(\d{2}):(\d{2}).(\d{3})Z$/;
  return dateRegex.test(input);
}

export const tinyMceDefaultOption = {
  promotion: false,
  base_url: '/tinymce',
  suffix: '.min',
  statusbar: false,
  elementpath: false,
  wordcount: false,
  branding: false,
  menubar: false,
  paste_data_images: true,
  browser_spellcheck: true,
  forced_root_block: '',
  extended_valid_elements: 'span[class|style],a[href|target=_blank }]|title',
  content_style: 'a { text-decoration: none } ',
  inline_styles: true,
  default_link_target: '_blank',
  target_list: false,
  convert_urls: false,
  relative_urls: true,
  automatic_uploads: true,
  plugins: [
    'lists autolink link preview noneditable',
    'searchreplace code fullscreen image code media',
    'paste'
  ],
  toolbar1: `undo redo | formatselect
    | bold italic underline forecolor backcolor
    | alignleft aligncenter alignright alignjustify
    | bullist numlist outdent indent`,
  toolbar2: `image video media
    | link removeformat code fullscreen`,
  content_css: [
  ],
  contextmenu: 'spellchecker',
};

export async function getBase64ImageFromUrl(imageUrl: string): Promise<string | ArrayBuffer | null> {
  var res = await fetch(imageUrl);
  var blob = await res.blob();

  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.addEventListener("load", function () {
      resolve(reader.result);
    }, false);

    reader.onerror = () => {
      return reject(this);
    };
    reader.readAsDataURL(blob);
  })
}

const DefaultAlertOption: SweetAlertOptions = {
  confirmButtonText: 'Yes',
  cancelButtonText: 'No',
  showCancelButton: true,
}

export const confirmDialog = (
  options: SweetAlertOptions = DefaultAlertOption,
  confirmButtonClass: string = 'btn-success',
  cancelButtonClass: string = 'btn-light ms-2'
): Observable<SweetAlertResult> => {

  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: confirmButtonClass ? `btn ${confirmButtonClass}` : 'btn',
      cancelButton: cancelButtonClass ? `btn ${cancelButtonClass}` : 'btn',
    },
    buttonsStyling: false
  });

  return from(swalWithBootstrapButtons.fire({ ...DefaultAlertOption, ...options } as SweetAlertOptions)).pipe(filter(res => !!res.value));
};

/**
 * Escape the five HTML-significant characters.
 *
 * Needed wherever user-supplied text reaches a sink that parses HTML rather than escaping
 * it for us — SweetAlert's `title`/`html` options and angular-calendar's `[innerHTML]`
 * title/tooltip rendering. Angular templates escape interpolations already, so this is NOT
 * needed for `{{ }}`. Pen test EVI-003-004.
 */
export function escapeHtml(value: string): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function confirmAction(actionName: string, name: string): Observable<SweetAlertResult> {
  const option: SweetAlertOptions = {
    title: `Are you sure you want to ${actionName} ${escapeHtml(name)}?`,
    text: 'You won\'t be able to revert this!',
    icon: 'warning',
    confirmButtonText: `Yes, ${actionName} it!`,
    cancelButtonText: 'No, cancel!',
    showCancelButton: true
  };

  return confirmDialog(option, 'btn-danger', 'btn-success ms-2')
}

export const confirmArchive = (name = '') => {
  const option: SweetAlertOptions = {
    title: name ? `Are you sure you want to archive ${escapeHtml(name)}?` : 'Are you sure you want to archive?',
    text: 'You won\'t be able to revert this!',
    icon: 'warning',
    confirmButtonText: 'Yes, archive it!',
    cancelButtonText: 'No, cancel!',
    showCancelButton: true
  };

  return confirmDialog(option, 'btn-danger', 'btn-success ms-2')
};

export const confirmUnArchive = (name = '') => {
  const option: SweetAlertOptions = {
    title: name ? `Are you sure you want to activate ${escapeHtml(name)}?` : 'Are you sure you want to activate?',
    text: 'You won\'t be able to revert this!',
    icon: 'warning',
    confirmButtonText: 'Yes, activate it!',
    cancelButtonText: 'No, cancel!',
    showCancelButton: true
  };

  return confirmDialog(option, 'btn-danger', 'btn-success ms-2')
};

export const confirmDelete =
  (
    name: string,
    confirmDeleteAllAlertComponent: ConfirmDeleteAllAlertComponent = null,
    showDeleteAllButton: boolean = false,
    // Optional replacement for the "won't be able to revert" line, for deletes that put
    // something back rather than remove it outright (deleting a decline reopens the referral).
    text: string = null
  ): Observable<SweetAlertResult> => {
    const confirmButtonClass: string = 'btn-danger mx-2';
    const cancelButtonClass: string = 'btn-success mx-2';

    const options: SweetAlertOptions = {
      title: `Are you sure you want to delete ${escapeHtml(name)}?`,
      text: text ?? 'You won\'t be able to revert this!',
      icon: 'warning',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
      showCancelButton: true,
      showConfirmButton: true,
      customClass: {
        confirmButton: confirmButtonClass ? `btn ${confirmButtonClass}` : 'btn',
        cancelButton: cancelButtonClass ? `btn ${cancelButtonClass}` : 'btn',
      }
    };

    if (confirmDeleteAllAlertComponent) {
      confirmDeleteAllAlertComponent.options = options;
      confirmDeleteAllAlertComponent.customClass = options.customClass as SweetAlertCustomClass;
      confirmDeleteAllAlertComponent.showDeleteAllButton = showDeleteAllButton;
      // Only the session calendar passes a confirmDeleteAllAlertComponent, and its second button
      // deletes the opened session plus every later one in the series — not the whole series.
      // Word it as the calendar convention people already know, so the two buttons read as
      // "just this one" vs "this one onwards".
      confirmDeleteAllAlertComponent.deleteAllButtonText = 'Yes, this and all future!';

      confirmDeleteAllAlertComponent.deleteSwal.swalOptions = options;

      return from(confirmDeleteAllAlertComponent.deleteSwal.fire()).pipe(filter(res => !!res.value))
    } else {
      return confirmDialog(options, 'btn-danger', 'btn-success ms-2')
    }
  }


export const showDeleteBlocked = (
  entityName: string,
  blockers: { category: string; count?: number }[]
): Promise<SweetAlertResult> => {
  const items = (blockers ?? [])
    .map(b => `<li>${escapeHtml(b.category)}${b.count != null ? ` (${b.count})` : ''}</li>`)
    .join('');
  const safeEntityName = escapeHtml(entityName);
  return Swal.fire({
    title: `Cannot Delete ${safeEntityName}`,
    icon: 'warning',
    html: `<p class="mb-2">This ${safeEntityName} has linked records and cannot be deleted:</p>
           <ul class="text-start mb-0">${items}</ul>`,
    confirmButtonText: 'OK',
    customClass: { confirmButton: 'btn btn-primary' }
  });
};

// Referral workflow: archiving a contact with an open referral is refused until it is accepted
// or declined, so the record carries a reason. Same readable-reasons dialog as delete.
export const showArchiveBlocked = (
  entityName: string,
  blockers: { category: string; count?: number }[]
): Promise<SweetAlertResult> => {
  const items = (blockers ?? [])
    .map(b => `<li>${escapeHtml(b.category)}${b.count != null ? ` (${b.count})` : ''}</li>`)
    .join('');
  const safeEntityName = escapeHtml(entityName);
  return Swal.fire({
    title: `Cannot Archive ${safeEntityName}`,
    icon: 'warning',
    html: `<p class="mb-2">This ${safeEntityName} has an open referral and cannot be archived until it is accepted or declined:</p>
           <ul class="text-start mb-0">${items}</ul>`,
    confirmButtonText: 'OK',
    customClass: { confirmButton: 'btn btn-primary' }
  });
};

export function downloadSourceFile(downloadFilePath: string, downloadFieName: string = "file") {
  const link = document.createElement('a');
  link.setAttribute('type', 'hidden');
  link.href = downloadFilePath;
  link.download = downloadFieName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function getDatepickerConfig(): BsDatepickerConfig {
  return Object.assign(new BsDatepickerConfig(),
    {
      dateInputFormat: DATE_FORMAT,
      showWeekNumbers: false,
      isAnimated: true
    }
  );
}

export const getScrollHeight = (header, extraoffset: number = 45): string => {
  return `calc(100vh - ${(header?.offsetHeight + extraoffset)}px)`;
};


export const isBetweenDate = (fromDate: Date, toDate: Date, givenDate: Date, includeStart = true, includeFinish = false): boolean => {

  let result = true;

  if (includeStart) {
    result = result && givenDate.getTime() >= fromDate.getTime();
  } else {
    result = result && givenDate.getTime() > fromDate.getTime();
  }

  if (includeFinish) {
    result = result && givenDate.getTime() <= toDate.getTime();
  } else {
    result = result && givenDate.getTime() < toDate.getTime();
  }

  return result;

};

export const prepareStringId = (val?: any): string => {
  let id = val;
  if (Array.isArray(id)) {
    id = id.length > 0 ? id.join(', ') : '';
  } else {
    id = val ? val.toString() : null;
  }
  return id;
};

/**
 * A licence term placed inside a sentence (pop-up and toast text): lower case, as the confirm helpers' wording
 * expects ("Are you sure you want to delete young person?"). A word in capitals is left as it is, so an
 * acronym term such as "CYP" is not turned into "cyp".
 */
export function termInSentence(term: string): string {
  return (term ?? '')
    .split(' ')
    .map((w) => (w.length > 1 && w === w.toUpperCase() && w !== w.toLowerCase() ? w : w.toLowerCase()))
    .join(' ');
}
